import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  BugReport as BugReportIcon,
  Email as EmailIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import apiClient from '../lib/api';
import { toast } from 'react-hot-toast';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
};

const statusColor = (status) => {
  switch (status) {
    case 'new': return 'default';
    case 'in_progress': return 'primary';
    case 'resolved': return 'success';
    case 'closed': return 'default';
    default: return 'default';
  }
};

const ReportRow = ({ report, onClick }) => {
  const description = report.description || '';
  const truncateLen = 80;
  const truncated = description.length <= truncateLen ? description : description.slice(0, truncateLen) + '…';
  const reporter = [report.userFirstName, report.userLastName].filter(Boolean).join(' ') || '—';
  const email = report.userEmail || '—';
  const status = report.status || 'new';

  return (
    <TableRow
      hover
      onClick={() => onClick(report)}
      sx={{
        cursor: 'pointer',
        '& > *': { verticalAlign: 'top' },
      }}
    >
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(report.createdAt)}</TableCell>
      <TableCell>
        <Chip label={STATUS_OPTIONS.find(s => s.value === status)?.label || status} size="small" color={statusColor(status)} />
      </TableCell>
      <TableCell>
        <Box>
          {reporter !== '—' && (
            <Typography variant="body2" fontWeight={500}>
              {reporter}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EmailIcon sx={{ fontSize: 14 }} />
            {email}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{truncated}</Typography>
      </TableCell>
    </TableRow>
  );
};

const IssueReportsList = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [tablePage, setTablePage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const paginatedReports = useMemo(() => {
    const start = tablePage * rowsPerPage;
    return reports.slice(start, start + rowsPerPage);
  }, [reports, tablePage, rowsPerPage]);

  useEffect(() => {
    if (reports.length === 0) {
      setTablePage(0);
      return;
    }
    const maxPage = Math.max(0, Math.ceil(reports.length / rowsPerPage) - 1);
    if (tablePage > maxPage) setTablePage(maxPage);
  }, [reports.length, rowsPerPage, tablePage]);

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getIssueReports();
      if (response.success) {
        setReports(response.reports || []);
      } else {
        throw new Error(response.message || 'Failed to load reports');
      }
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to load issue reports';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  const handleOpenDetail = (report) => {
    setSelectedReport(report);
    setEditStatus(report.status || 'new');
    setEditNotes(report.notes || '');
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedReport(null);
    setDeleteConfirmOpen(false);
  };

  const handleSave = async () => {
    if (!selectedReport?.id) return;
    setSaving(true);
    try {
      const response = await apiClient.updateIssueReport(selectedReport.id, {
        status: editStatus,
        notes: editNotes,
      });
      if (response.success && response.report) {
        setReports(prev => prev.map(r => r.id === selectedReport.id ? response.report : r));
        setSelectedReport(response.report);
        toast.success('Updated');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to update';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedReport?.id) return;
    setDeleting(true);
    try {
      await apiClient.deleteIssueReport(selectedReport.id);
      setReports(prev => prev.filter(r => r.id !== selectedReport.id));
      handleCloseDetail();
      toast.success('Issue report deleted');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to delete';
      toast.error(msg);
    } finally {
      setDeleting(false);
      setDeleteConfirmOpen(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
        <CircularProgress size={24} />
        <Typography color="text.secondary">Loading issue reports…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }} action={
        <IconButton color="inherit" size="small" onClick={loadReports}>
          <RefreshIcon />
        </IconButton>
      }>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BugReportIcon color="primary" />
          Reported issues
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={`${reports.length} report${reports.length !== 1 ? 's' : ''}`} size="small" />
          <Tooltip title="Refresh">
            <IconButton onClick={loadReports} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {reports.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No issue reports yet.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Reported by</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedReports.map((report) => (
                <ReportRow key={report.id} report={report} onClick={handleOpenDetail} />
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={reports.length}
            page={tablePage}
            onPageChange={(_, newPage) => setTablePage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setTablePage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </TableContainer>
      )}

      {/* Detail / edit dialog */}
      <Dialog open={detailOpen} onClose={handleCloseDetail} maxWidth="sm" fullWidth>
        <DialogTitle>Issue report</DialogTitle>
        <DialogContent>
          {selectedReport && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {formatDate(selectedReport.createdAt)}
                {selectedReport.updatedAt && ` · Updated ${formatDate(selectedReport.updatedAt)}`}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Reported by</Typography>
                <Typography variant="body2">
                  {[selectedReport.userFirstName, selectedReport.userLastName].filter(Boolean).join(' ') || '—'}
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EmailIcon sx={{ fontSize: 14 }} />
                  {selectedReport.userEmail || '—'}
                </Typography>
              </Box>
              <Typography variant="subtitle2" color="text.secondary">Description</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                {selectedReport.description || '—'}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editStatus}
                  label="Status"
                  onChange={(e) => setEditStatus(e.target.value)}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Notes"
                placeholder="Internal notes for this report…"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                variant="outlined"
                size="small"
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDetail}>Cancel</Button>
          <Button
            onClick={handleDeleteClick}
            color="error"
            startIcon={<DeleteIcon />}
            disabled={deleting}
          >
            Delete
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            sx={{ backgroundColor: '#2c4575', '&:hover': { backgroundColor: '#1e3154' } }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => !deleting && setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete this report?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IssueReportsList;
